import React, {useState} from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {LazyQueryExecFunction} from '@apollo/client';
import ActionModal from '../../components/ContactActions/ActionModal';
import AutoField, {
  ActionType,
  CheckboxGroupField,
} from '../../components/AutoForm/AutoFieldNew';

import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {
  SearchReportPacks,
  SearchReportPacksVariables,
} from '../../schema-types/SearchReportPacks';
import {useAllAdvisers} from '../../hooks/useAllAdvisers';
import {ReportPackDashboardValues} from './ReportPackDashboardTypes';
import {UPDATES} from '../../shared/UPDATES';

export const advisersField: CheckboxGroupField = {
  type: ActionType.checkboxGroup,
  options: [],
  id: 'adviserIds',
  label: 'Please select the adviser(s)',
};

export default function ReportPackDashboardFiltersModal({
  isOpen,
  close,
  fetch,
}: {
  isOpen: boolean;
  close: () => void;
  fetch: LazyQueryExecFunction<SearchReportPacks, SearchReportPacksVariables>;
}) {
  const {allAdvisers, loading: advisersLoading} = useAllAdvisers();
  const {values, setFieldValue} = useAutoForm<ReportPackDashboardValues>();
  const [search, setSearch] = useState('');

  const filteredAdvisers = allAdvisers
    .filter((a) => a.name?.toLocaleLowerCase().includes(search.toLocaleLowerCase()))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const filteredAdviserIds = filteredAdvisers
    .map((x) => String(x.id || ''))
    .filter((x) => !!x);
  const isAllSelected =
    filteredAdviserIds.length > 0 &&
    filteredAdviserIds.every((id) => values.adviserIds.includes(id));
  const isIndeterminate =
    values.adviserIds.length > 0 &&
    values.adviserIds.length < filteredAdviserIds.length &&
    filteredAdviserIds.some((id) => values.adviserIds.includes(id));

  const handleSelectAll = async () => {
    if (isAllSelected) {
      // Deselect all filtered advisers
      const remainingIds = values.adviserIds.filter(
        (id) => !filteredAdviserIds.includes(id)
      );
      await setFieldValue(UPDATES._2005, 'adviserIds', remainingIds);
    } else {
      // Select all filtered advisers
      const newIds = [...new Set([...values.adviserIds, ...filteredAdviserIds])];
      await setFieldValue(UPDATES._2006, 'adviserIds', newIds);
    }
  };

  return (
    <ActionModal title="Filter by Adviser" isOpen={isOpen} close={close} autoHeight wide>
      {advisersLoading && <Typography>Loading Advisers...</Typography>}
      <TextField
        value={search}
        autoFocus
        onChange={async (event) => {
          const searchTerm = event.target.value;
          setSearch(searchTerm);
        }}
        placeholder="Search advisers"
        style={{minWidth: 200}}
        margin="normal"
        inputProps={{
          type: 'search',
        }}
      />
      {!advisersLoading && filteredAdviserIds.length > 0 && (
        <Box padding={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={handleSelectAll}
              />
            }
            label="Select All"
          />
        </Box>
      )}
      {!advisersLoading && (
        <Box overflow="scroll" padding={1} height="500px">
          <AutoField
            key={search}
            field={{
              ...advisersField,
              options: filteredAdvisers.map((x) => ({
                label: x.name || 'N/A',
                value: x.id || '',
              })),
            }}
          />
        </Box>
      )}
      <Box display="flex" justifyContent="end">
        <Button
          color="secondary"
          variant="outlined"
          onClick={async () => {
            await setFieldValue(UPDATES._2007, 'adviserIds', []);
            await fetch({
              variables: {
                adviserIds: [],
                contactNameIncludes: values.search,
                statuses: values.statuses,
              },
            });
            close();
          }}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          sx={{marginLeft: 2}}
          onClick={async () => {
            await fetch({
              variables: {
                adviserIds: values.adviserIds,
                contactNameIncludes: values.search,
                statuses: values.statuses,
              },
            });
            close();
          }}
        >
          Filter
        </Button>
      </Box>
    </ActionModal>
  );
}
